/*
 * The manager's screens.
 *
 * The dashboard answers "is anything wrong?" — not "how are we doing?". Every line on it is
 * something a person can act on today.
 *
 * The live board is meant to be left up on a screen while a round is happening: who has been
 * done, who is left, how long it is taking, and what has just been recorded.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from './api.ts';

type RoundBoard = {
  roundId: string;
  name: string;
  startsAt: string;
  dueAt: string;
  endsAt: string;
  state: 'finished' | 'now' | 'later';
  total: number;
  given: number;
  notGiven: number;
  overdue: number;
  waiting: number;
  firstAt: string | null;
  lastAt: string | null;
  staff: string[];
};

type ResidentLine = {
  residentId: string;
  name: string;
  room: string;
  total: number;
  done: number;
  overdue: number;
  waiting: Array<{ name: string; dueAt: string }>;
  lastAt: string | null;
};

type Attention = { kind: string; text: string; count: number; urgent: boolean };

type Dashboard = {
  date: string;
  today: { total: number; given: number; notGiven: number; overdue: number };
  rounds: RoundBoard[];
  attention: Attention[];
  week: Array<{ date: string; onTime: number; late: number; notGiven: number }>;
  reasons: Array<{ code: string; count: number; label: string }>;
  cycle: { number: number; day: number | null; days: number; startsOn: string; endsOn: string } | null;
};

type Live = {
  at: string;
  rounds: RoundBoard[];
  current: RoundBoard | null;
  residents: ResidentLine[];
  activity: Array<{ at: string; resident: string; medicine: string; outcome: string; code: string | null; by: string }>;
};

export function DashboardScreen({ homeId }: { homeId: string }) {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await api.get<Dashboard>(`/api/homes/${homeId}/dashboard`));
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the dashboard.');
    }
  }, [homeId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <p className="error" role="alert">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const urgent = data.attention.filter((row) => row.urgent);
  const rest = data.attention.filter((row) => !row.urgent);

  return (
    <section>
      <div className="head">
        <div>
          <h1>Today</h1>
          <p className="muted small">
            {formatDate(data.date)}
            {data.cycle?.day ? ` · cycle day ${data.cycle.day} of ${data.cycle.days}` : ''}
          </p>
        </div>
        <button type="button" className="quiet" onClick={() => void load()}>Refresh</button>
      </div>

      {!data.attention.length ? (
        <p className="notice">Nothing needs attention. Everything due so far has been recorded.</p>
      ) : null}

      {urgent.length ? (
        <ul className="list">
          {urgent.map((row) => (
            <li key={row.kind}>
              <div className="row static dose overdue">
                <span className="row-main">{row.text}</span>
                <span className="chip warn">Needs attention</span>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {rest.length ? (
        <ul className="list">
          {rest.map((row) => (
            <li key={row.kind}>
              <div className="row static">
                <span className="row-main">{row.text}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <h2>Rounds today</h2>
      <ul className="list">
        {data.rounds.map((round) => (
          <li key={round.roundId}>
            <div className={`row static column dose ${round.overdue ? 'overdue' : round.state === 'now' ? 'due' : ''}`}>
              <div className="row-line">
                <span className="row-main">
                  {round.name} {round.state === 'now' ? <span className="chip">happening now</span> : null}
                </span>
                <span className="time">{round.startsAt}–{round.endsAt}</span>
              </div>
              <div className="bar" aria-hidden="true">
                <span style={{ width: `${round.total ? Math.round(((round.given + round.notGiven) / round.total) * 100) : 0}%` }} />
              </div>
              <div className="chips">
                <span className="chip">{round.given + round.notGiven} of {round.total} recorded</span>
                {round.notGiven ? <span className="chip warn">{round.notGiven} not given</span> : null}
                {round.overdue ? <span className="chip warn">{round.overdue} overdue</span> : null}
                {round.firstAt ? <span className="chip">{round.firstAt} to {round.lastAt}</span> : null}
                {round.staff.length ? <span className="chip">{round.staff.join(', ')}</span> : null}
              </div>
            </div>
          </li>
        ))}
      </ul>

      <h2>The last week</h2>
      <Week week={data.week} />

      {data.reasons.length ? (
        <>
          <h3>Why doses were not given</h3>
          <ul className="list">
            {data.reasons.map((row) => (
              <li key={row.code}>
                <div className="row static">
                  <span className="row-main">{row.label}</span>
                  <span className="time">{row.count}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}

/* One bar per day: given on time, given late, and not given. */
function Week({ week }: { week: Dashboard['week'] }) {
  if (!week.length) return <p className="muted">Nothing recorded yet.</p>;
  const most = Math.max(...week.map((day) => day.onTime + day.late + day.notGiven), 1);

  return (
    <>
      <div className="week">
        {week.map((day) => {
          const total = day.onTime + day.late + day.notGiven;
          return (
            <div className="week-row" key={day.date}>
              <span className="week-day">{shortDay(day.date)}</span>
              <span className="week-bar" style={{ width: `${(total / most) * 100}%` }}>
                <span className="on-time" style={{ flex: day.onTime }} title={`${day.onTime} on time`} />
                <span className="late" style={{ flex: day.late }} title={`${day.late} late`} />
                <span className="not-given" style={{ flex: day.notGiven }} title={`${day.notGiven} not given`} />
              </span>
              <span className="week-count">{total}</span>
            </div>
          );
        })}
      </div>
      <p className="legend small">
        <span className="key on-time" /> Given on time
        <span className="key late" /> Given after the round closed
        <span className="key not-given" /> Not given
      </p>
    </>
  );
}

export function LiveScreen({ homeId }: { homeId: string }) {
  const [data, setData] = useState<Live | null>(null);
  const [roundId, setRoundId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const query = roundId ? `?roundId=${roundId}` : '';
      const result = await api.get<Live>(`/api/homes/${homeId}/live${query}`);
      setData(result);
      if (!roundId && result.current) setRoundId(result.current.roundId);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the board.');
    }
  }, [homeId, roundId]);

  /* Poll while the screen is being looked at, and stop when it is not. */
  useEffect(() => {
    void load();
    const tick = () => {
      if (!document.hidden) void load();
    };
    timer.current = window.setInterval(tick, 10_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  const round = data?.rounds.find((r) => r.roundId === roundId) ?? data?.current ?? null;
  const done = round ? round.given + round.notGiven : 0;
  const percent = round?.total ? Math.round((done / round.total) * 100) : 0;

  return (
    <section className="live">
      <div className="head">
        <div>
          <h1>Round board</h1>
          <p className="muted small">Updates on its own · {data?.at ?? '…'}</p>
        </div>
        <button type="button" className="quiet" onClick={() => void load()}>Refresh now</button>
      </div>

      {error ? <p className="error" role="alert">{error}</p> : null}

      <nav className="tabs">
        {(data?.rounds ?? []).map((r) => (
          <button type="button" key={r.roundId} className={r.roundId === roundId ? 'on' : ''}
            onClick={() => setRoundId(r.roundId)}>
            {r.name}
            {r.state === 'now' ? <span className="chip">now</span> : null}
          </button>
        ))}
      </nav>

      {round ? (
        <div className="live-summary">
          <div className="stat">
            <b>{done} of {round.total}</b>
            <span>recorded</span>
          </div>
          <div className="stat">
            <b>{round.overdue}</b>
            <span>overdue</span>
          </div>
          <div className="stat">
            <b>{round.notGiven}</b>
            <span>not given</span>
          </div>
          <div className="stat">
            <b>{round.firstAt ?? '—'}{round.lastAt && round.lastAt !== round.firstAt ? `–${round.lastAt}` : ''}</b>
            <span>started to last recorded</span>
          </div>
          <div className="stat">
            <b>{round.staff.length ? round.staff.join(', ') : '—'}</b>
            <span>recording</span>
          </div>
        </div>
      ) : null}

      <div className="bar big" aria-label={`${percent}% recorded`}>
        <span style={{ width: `${percent}%` }} />
      </div>

      <div className="live-grid">
        {(data?.residents ?? []).map((line) => {
          const state = line.total === 0 ? 'none' : line.overdue ? 'overdue' : line.done >= line.total ? 'done' : 'waiting';
          return (
            <div className={`live-card ${state}`} key={line.residentId}>
              <div className="row-line">
                <strong>{line.name}</strong>
                <span className="muted small">{line.room ? `Room ${line.room}` : ''}</span>
              </div>
              {line.total === 0 ? (
                <p className="muted small">Nothing due</p>
              ) : (
                <>
                  <p className="live-count">{line.done} of {line.total}</p>
                  {line.waiting.length ? (
                    <ul className="tray-items">
                      {line.waiting.map((item) => (
                        <li key={`${item.name}-${item.dueAt}`}>
                          <span className="time">{item.dueAt}</span> {item.name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="muted small">Finished{line.lastAt ? ` at ${line.lastAt}` : ''}</p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      <h2>Just recorded</h2>
      <ul className="list feed">
        {(data?.activity ?? []).map((row, index) => (
          <li key={`${row.at}-${index}`}>
            <div className="row static">
              <span className="row-main">
                <span className="time">{row.at}</span> {row.resident} · {row.medicine}
              </span>
              <span className={`chip ${row.outcome === 'given' ? 'done' : 'warn'}`}>
                {row.outcome === 'given' ? 'Given' : `Not given (${row.code})`} · {row.by}
              </span>
            </div>
          </li>
        ))}
        {data && !data.activity.length ? <li><p className="muted">Nothing recorded yet today.</p></li> : null}
      </ul>
    </section>
  );
}

function shortDay(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' });
}

function formatDate(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}
